import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '../config/api.config';
import {
  TranslationService,
  SupportedLanguage,
  LanguageDetection,
  TranslationRequest,
  TranslationResponse,
  ApiResponse,
  TranslationCache,
  UseAutoTranslationReturn
} from '../types/translation';

class AutoTranslationService implements TranslationService {
  private cache = new Map<string, TranslationCache>();
  private readonly cacheExpiry = 10 * 60 * 1000; // 10 min — DB cache côté serveur est permanent

  constructor() {
    console.log('🔍 Service de traduction initialisé');
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('🔑 [Translation] Token ajouté aux headers');
    } else {
      console.log('ℹ️ [Translation] Pas de token - requête publique');
    }
    
    return headers;
  }

  async getSupportedLanguages(): Promise<SupportedLanguage[]> {
    try {
      // Utiliser API_CONFIG pour construire l'URL
      const url = API_CONFIG.getApiUrl('/translation/public/languages');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        console.warn(`❌ Erreur HTTP ${response.status}: ${response.statusText}`);
        return [];
      }

      const data: ApiResponse<SupportedLanguage[]> = await response.json();

      if (data.success && data.data) {
        return data.data;
      } else {
        console.warn('⚠️ Erreur langues supportées:', data.message);
        return [];
      }
    } catch (error) {
      console.error('❌ Erreur réseau langues:', error);
      return [];
    }
  }

  async translateText(text: string, targetLang: string, sourceLang?: string): Promise<string> {
    if (!text?.trim()) return text;

    // Clé de cache
    const cacheKey = `${text}_${sourceLang || 'auto'}_${targetLang}`;
    
    // Vérifier le cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)!;
      if (Date.now() - cached.timestamp < this.cacheExpiry) {
        console.log(`📋 [Translation] Cache hit: "${text}" -> "${cached.text}"`);
        return cached.text;
      }
      this.cache.delete(cacheKey);
    }

    try {
      const requestBody: TranslationRequest = {
        text,
        targetLanguage: targetLang,
        sourceLanguage: sourceLang
      };

      // Utiliser API_CONFIG pour l'URL
      const url = API_CONFIG.getApiUrl('/translation/public/translate');
      console.log(`🔄 [Translation] Appel API: ${url}`);
      console.log(`🔄 [Translation] Requête: "${text}" (${sourceLang || 'auto'} -> ${targetLang})`);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log(`📡 [Translation] Réponse HTTP: ${response.status}`);

      if (!response.ok) {
        console.error(`❌ [Translation] Erreur HTTP ${response.status}: ${response.statusText}`);
        return text;
      }

      const data: ApiResponse<TranslationResponse> = await response.json();

      if (data.success && data.data) {
        // Mettre en cache
        this.cache.set(cacheKey, {
          text: data.data.translatedText,
          timestamp: Date.now()
        });

        return data.data.translatedText;
      } else {
        console.warn('⚠️ [Translation] Erreur API:', data.message);
        return text;
      }
    } catch (error) {
      console.error('❌ [Translation] Erreur réseau:', error);
      return text;
    }
  }

  async detectLanguage(text: string): Promise<LanguageDetection | null> {
    if (!text?.trim()) return null;

    try {
      // Utiliser API_CONFIG
      const response = await fetch(API_CONFIG.getApiUrl('/translation/detect'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ text })
      });

      const data: ApiResponse<LanguageDetection> = await response.json();

      if (data.success && data.data) {
        return data.data;
      } else {
        console.warn('Erreur détection langue:', data.message);
        return null;
      }
    } catch (error) {
      console.error('Erreur réseau détection:', error);
      return null;
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

const translationService = new AutoTranslationService();

export const useAutoTranslation = (): UseAutoTranslationReturn => {
  const { i18n } = useTranslation();
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguage[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  // Fonction pour charger les langues supportées
  const loadSupportedLanguages = useCallback(async () => {
    
    try {
      const languages = await translationService.getSupportedLanguages();
     
      setSupportedLanguages(languages);
    } catch (error) {
      console.error('❌ Erreur chargement langues:', error);
      setSupportedLanguages([]);
    }
  }, []);

  // Fonction pour changer de langue
  const changeLanguage = useCallback(async (langCode: string) => {
  
    try {
      setIsTranslating(true);
      await i18n.changeLanguage(langCode);
      setCurrentLanguage(langCode);
     
    } catch (error) {
      console.error('❌ Erreur changement langue:', error);
    } finally {
      setIsTranslating(false);
    }
  }, [i18n]);

  // Fonction pour traduire du texte
  const translateText = useCallback(async (text: string, targetLang?: string): Promise<string> => {
    const target = targetLang || currentLanguage;
    if (!text?.trim()) return text;

    

    try {
      setIsTranslating(true);
      const result = await translationService.translateText(text, target, 'fr');
      console.log(`✅ [translateText] Résultat: "${result}"`);
      return result;
    } catch (error) {
      console.error('❌ [translateText] Erreur:', error);
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, [currentLanguage]);

  // Charger les langues au montage du composant
  useEffect(() => {
    
    loadSupportedLanguages();
  }, [loadSupportedLanguages]);

  // Synchroniser avec i18n
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      console.log(`🌍 i18n langue changée: ${lng}`);
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Détection automatique de la langue du navigateur
  useEffect(() => {
    const detectBrowserLanguage = async (): Promise<void> => {
      if (supportedLanguages.length === 0) return;

      try {
        const browserLang = navigator.language.split('-')[0];
        const supportedCodes = supportedLanguages.map(lang => lang.code);
        
        
        
        if (supportedCodes.includes(browserLang) && currentLanguage !== browserLang) {
          
          await changeLanguage(browserLang);
        } else {
          //console.log(`ℹ️ Pas de changement nécessaire: ${currentLanguage}`);
        }
      } catch (error) {
        console.error('❌ Erreur détection langue navigateur:', error);
      }
    };

    detectBrowserLanguage();
  }, [supportedLanguages, currentLanguage, changeLanguage]);

  return {
    supportedLanguages,
    currentLanguage,
    isTranslating,
    changeLanguage,
    translateText
  };
};

export default translationService;